'use client';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/Command/Command';
import { useClient, useDebounce } from '@/hooks';
import { useQuery } from 'react-query';
import { PlacesI } from '@/types/cyclone/requests';
import { Row } from '../Row';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faXmark, faSpinner, faSearch } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import { SupportedCountriesE } from '@/types/cyclone/models';
import { escapeForSelector } from '@/utils';
import { APICallProtector, generateUUID } from '@/utils/apiProtection';
import useLog from '@/hooks/useLog';

export interface ServiceAddress {
  address: string | undefined;
  address_details: string | undefined;
  latitude: string | undefined;
  longitude: string | undefined;
  neighborhood: string | undefined;
  country: SupportedCountriesE | undefined;
  state: string | undefined;
}

interface InputAddressProps {
  selectedPlace?: ServiceAddress;
  onSelect: (address?: ServiceAddress) => void;
  disabled?: boolean;
  country: SupportedCountriesE | null;
  state?: string;
  useAutocomplete?: boolean; // Option to toggle between regular search and autocomplete
}

export const InputAddress: React.FC<InputAddressProps> = ({
  selectedPlace,
  disabled = false,
  country,
  state,
  onSelect,
  useAutocomplete = true // Default to using autocomplete
}) => {
  const { client } = useClient();
  const { logAndNotify } = useLog();
  const [value, setValue] = useState(selectedPlace?.address || '');
  const [debouncedValue, setDebouncedValue] = useState('');
  const [isEdition, setIsEdition] = useState(!selectedPlace);
  const [sessionToken, setSessionToken] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // API Protection
  const [componentId] = useState(() => `input-address-${generateUUID()}`);
  const [protectionError, setProtectionError] = useState<string | null>(null);
  const protector = APICallProtector.getInstance();

  // State for selected place from the dropdown
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);
  const [selectedPlaceInfo, setSelectedPlaceInfo] = useState<{
    formattedAddress: string;
    neighborhood?: string;
  } | null>(null);

  // Initialize session token when component mounts or when editing mode starts
  useEffect(() => {
    if (isEdition && !sessionToken) {
      setSessionToken(generateUUID());
    }
  }, [isEdition, sessionToken]);

  const capitalizeAddress = (text: string) => {
    return text
      .split(' ')
      .map((word) => {
        const lowerCaseWords = ['de', 'del', 'la', 'las', 'los', 'y', 'e', 'o', 'u'];
        return lowerCaseWords.includes(word.toLowerCase())
          ? word.toLowerCase()
          : word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  };

  // Build a user-friendly address from components
  const buildFriendlyAddress = (place: PlacesI): string => {
    // If address components are missing (which can happen with autocomplete results)
    if (!place.addressComponents || place.addressComponents.length === 0) {
      // Use the formatted address or display name text
      return place.formattedAddress || place.displayName.text;
    }

    // Extract components by type
    const getComponent = (types: string[]): string | undefined => {
      const component = place.addressComponents.find((comp) =>
        types.some((type) => comp.types.includes(type))
      );
      return component?.longText;
    };

    // Get primary components
    const street = getComponent(['route']);
    const streetNumber = getComponent(['street_number']);
    const neighborhood = getComponent(['sublocality_level_1', 'sublocality']);
    const locality = getComponent(['locality']);
    const adminArea = getComponent(['administrative_area_level_1']);

    // Format address parts
    const parts: string[] = [];

    // Part 1: Street + Number
    if (street && streetNumber) {
      parts.push(`${street} ${streetNumber}`);
    } else {
      // Fallback to displayName if street components not available
      parts.push(place.displayName.text);
    }

    // Part 2: Neighborhood or locality
    if (neighborhood) {
      parts.push(neighborhood);
    } else if (locality) {
      parts.push(locality);
    }

    // Part 3: Province/State
    if (adminArea && adminArea !== locality) {
      parts.push(adminArea);
    }

    // Join with commas for a single line display
    return parts.join(', ');
  };

  // Use a longer debounce to reduce API calls
  const debouncedUpdate = useDebounce((searchVal: string) => {
    setDebouncedValue(searchVal);
    setProtectionError(null); // Clear protection errors when user types
  }, 800); // Increased from 300ms to 800ms

  const onChangeLocationInput = useCallback(
    (place: string) => {
      setValue(place);
      debouncedUpdate(place);
    },
    [debouncedUpdate]
  );

  useEffect(() => {
    if (isEdition && inputRef.current) {
      inputRef.current.select();
    }
  }, [isEdition]);

  useEffect(() => {
    if (selectedPlace?.address) setValue(selectedPlace.address);
  }, [selectedPlace]);

  React.useLayoutEffect(() => {
    if (selectedPlace?.address) setIsEdition(false);
  }, [selectedPlace]);

  // Enhanced query function with protection
  const fetchPlaces = useCallback(async () => {
    if (!debouncedValue) return [];

    // Check API protection
    const protection = protector.canMakeCall(componentId);
    if (!protection.allowed) {
      setProtectionError(protection.reason!);
      logAndNotify(`Google Places API limite excedido: ${protection.reason}`);
      throw new Error(protection.reason);
    }

    try {
      // Record the API call
      protector.recordCall(componentId);

      // Choose the appropriate endpoint based on useAutocomplete flag
      const endpoint = useAutocomplete ? 'places/autocomplete' : 'places';

      // Build the URL with session token
      let url = `google/${endpoint}/${encodeURIComponent(debouncedValue)}?country=${country}`;
      // Add state to the query if provided
      if (state) {
        url += `&state=${encodeURIComponent(state)}`;
      }
      if (sessionToken) {
        url += `&sessionToken=${sessionToken}`;
      }

      const result = await client<PlacesI[]>(url);

      return result;
    } catch (err) {
      protector.recordFailure(componentId);
      throw err;
    }
  }, [debouncedValue, country, state, sessionToken, useAutocomplete, componentId, client, protector]);

  // Query for fetching place suggestions based on user input
  const {
    data: locationResponses,
    error,
    isFetching,
    isSuccess
  } = useQuery(
    ['location', debouncedValue, country, state, sessionToken, useAutocomplete, componentId], // Added componentId to prevent cross-component interference
    fetchPlaces,
    {
      enabled: debouncedValue.length > 2 && isEdition && !protectionError, // Increased minimum length from 0 to 2
      retry: (failureCount, error) => {
        // Don't retry if it's a protection error
        if (error instanceof Error && error.message.includes('limit exceeded')) {
          return false;
        }
        return failureCount < 2; // Reduced retry attempts from 3 to 2
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff
      refetchOnWindowFocus: false,
      staleTime: 60000, // Increased from 30s to 60s
      cacheTime: 300000, // 5 minutes cache
      keepPreviousData: true,
      onError: (error) => {
        console.error('Places search error:', error);
        if (error instanceof Error) {
          setProtectionError(error.message);
        }
      }
    }
  );

  // Enhanced place details query with protection
  const fetchPlaceDetails = useCallback(async () => {
    if (!selectedPlaceId || !sessionToken) return null;

    // Check API protection
    const protection = protector.canMakeCall(componentId);
    if (!protection.allowed) {
      setProtectionError(protection.reason!);
      throw new Error(protection.reason);
    }

    try {
      protector.recordCall(componentId);
      return await client<PlacesI>(`google/place/${selectedPlaceId}?sessionToken=${sessionToken}`);
    } catch (err) {
      protector.recordFailure(componentId);
      throw err;
    }
  }, [selectedPlaceId, sessionToken, componentId, client, protector]);

  // Query for fetching place details when a place is selected
  const { isLoading: isLoadingDetails } = useQuery(
    ['placeDetails', selectedPlaceId, sessionToken, componentId],
    fetchPlaceDetails,
    {
      enabled: !!selectedPlaceId && !!sessionToken && useAutocomplete && !protectionError,
      retry: (failureCount, error) => {
        if (error instanceof Error && error.message.includes('limit exceeded')) {
          return false;
        }
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data && selectedPlaceInfo) {
          const addressData: ServiceAddress = {
            address: selectedPlaceInfo.formattedAddress,
            address_details: undefined,
            latitude: data.location?.latitude?.toString() || '',
            longitude: data.location?.longitude?.toString() || '',
            neighborhood: selectedPlaceInfo.neighborhood,
            country: country || undefined,
            state: state
          };

          // Call onSelect with the complete address data
          onSelect(addressData);

          // Reset for next search
          setSelectedPlaceId(null);
          setSelectedPlaceInfo(null);
          setSessionToken('');
          setIsEdition(false);
        }
      },
      onError: (error) => {
        console.error('Place details error:', error);
        if (error instanceof Error) {
          setProtectionError(error.message);
        }

        // Handle error by still submitting what we have
        if (selectedPlaceInfo) {
          const addressData: ServiceAddress = {
            address: selectedPlaceInfo.formattedAddress,
            address_details: undefined,
            latitude: '',
            longitude: '',
            neighborhood: selectedPlaceInfo.neighborhood,
            country: country || undefined,
            state: state
          };

          onSelect(addressData);

          // Reset for next search
          setSelectedPlaceId(null);
          setSelectedPlaceInfo(null);
          setSessionToken('');
          setIsEdition(false);
        }
      }
    }
  );

  // Extract the neighborhood from address components
  const getNeighborhood = (addressComponents: any[] = []): string | undefined => {
    return addressComponents.find(
      (component) =>
        component.types.includes('sublocality_level_1') ||
        component.types.includes('sublocality') ||
        component.types.includes('neighborhood')
    )?.longText;
  };

  // Handle place selection
  const handleSelectPlace = (placeId: string) => {
    const selected = locationResponses?.find((place) => place.id === placeId);

    if (selected) {
      // Format the address
      const friendlyAddress = buildFriendlyAddress(selected);
      const formattedAddress = capitalizeAddress(friendlyAddress);

      // Get neighborhood
      const neighborhood = getNeighborhood(selected.addressComponents);

      // Update UI immediately
      setValue(formattedAddress);

      if (useAutocomplete && (!selected.location || !selected.location.latitude)) {
        // For autocomplete flow: Store the selected place ID and info for the detail query
        setSelectedPlaceId(placeId);
        setSelectedPlaceInfo({
          formattedAddress,
          neighborhood
        });
        // The place details query will run automatically and handle selection
      } else {
        // For regular search: We already have all the information we need
        const addressData: ServiceAddress = {
          address: formattedAddress,
          address_details: undefined,
          latitude: selected.location?.latitude?.toString() || '',
          longitude: selected.location?.longitude?.toString() || '',
          neighborhood: neighborhood,
          country: country || undefined,
          state: state
        };

        setIsEdition(false);
        onSelect(addressData);
        setSessionToken('');
      }
    }
  };

  const handleEditPlace = () => {
    setIsEdition(true);
    setValue(selectedPlace?.address || '');
    setDebouncedValue(selectedPlace?.address || '');
    // Generate a new session token for this editing session
    setSessionToken(generateUUID());
  };

  const handleCancelEdition = () => {
    setIsEdition(false);
    setValue(selectedPlace?.address || '');
    // Reset the session token when canceling
    setSessionToken('');
    setSelectedPlaceId(null);
    setSelectedPlaceInfo(null);
  };

  // Enhanced state filtering function
  const filteredByState = locationResponses?.filter((place) => {
    if (!state) return true;

    // Method 1: Check address components (more accurate for regular places search)
    if (place.addressComponents && place.addressComponents.length > 0) {
      const adminArea = place.addressComponents.find((comp) =>
        comp.types.includes('administrative_area_level_1')
      )?.longText;

      if (adminArea) {
        // Normalize strings for comparison (remove accents, etc.)
        const normalizedAdminArea = adminArea
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase();
        const normalizedState = state
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase();

        // Use more flexible matching options
        // Option 1: Exact match
        if (normalizedAdminArea === normalizedState) return true;

        // Option 2: Contains match (for partial state names)
        if (normalizedAdminArea.includes(normalizedState) || normalizedState.includes(normalizedAdminArea))
          return true;

        // Option 3: Check for common abbreviations
        const stateAbbreviations: Record<string, string[]> = {
          'buenos aires': [
            'bs as',
            'bsas',
            'ba',
            'bs. as.',
            'pcia de buenos aires',
            'provincia de buenos aires'
          ],
          'ciudad autónoma de buenos aires': ['caba', 'capital federal', 'ciudad de buenos aires'],
          córdoba: ['cba', 'cdoba', 'cordoba'],
          'santa fe': ['sta fe', 'sta. fe'],
          'entre ríos': ['entre rios'],
          'río negro': ['rio negro'],
          'santiago del estero': ['sgo del estero', 'santiago estero']
          // Add more as needed for your supported states
        };

        // Check if the state exists in our abbreviations map
        if (stateAbbreviations[normalizedState]) {
          const matchAbbreviations = stateAbbreviations[normalizedState];
          if (matchAbbreviations.some((abbr) => normalizedAdminArea.includes(abbr))) return true;
        }

        // Check reverse - if any abbreviation is in our state name
        for (const [stateName, abbrs] of Object.entries(stateAbbreviations)) {
          if (abbrs.includes(normalizedState) && normalizedAdminArea.includes(stateName)) return true;
        }

        return false;
      }
    }

    // Method 2: Check formatted address for autocomplete results without address components
    const addressText = place.formattedAddress || place.displayName?.text || '';
    const normalizedAddressText = addressText
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
    const normalizedState = state
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

    // Try different matching strategies:

    // 1. Look for state preceded by comma (common format: "City, State")
    const commaStatePattern = new RegExp(`\\,\\s*${normalizedState}\\b`, 'i');
    if (commaStatePattern.test(normalizedAddressText)) return true;

    // 2. Simple word boundary check
    const wordBoundaryPattern = new RegExp(`\\b${normalizedState}\\b`, 'i');
    if (wordBoundaryPattern.test(normalizedAddressText)) return true;

    // 3. Check for abbreviations in the address string
    const stateAbbreviations: Record<string, string[]> = {
      'buenos aires': ['bs as', 'bsas', 'ba', 'bs. as.', 'pcia de buenos aires', 'provincia de buenos aires'],
      'ciudad autónoma de buenos aires': ['caba', 'capital federal', 'ciudad de buenos aires'],
      córdoba: ['cba', 'cdoba', 'cordoba'],
      'santa fe': ['sta fe', 'sta. fe'],
      'entre ríos': ['entre rios'],
      'río negro': ['rio negro'],
      'santiago del estero': ['sgo del estero', 'santiago estero']
      // Add more as needed for your supported states
    };

    // Check if the state exists in our abbreviations map
    if (stateAbbreviations[normalizedState]) {
      const matchAbbreviations = stateAbbreviations[normalizedState];
      if (matchAbbreviations.some((abbr) => normalizedAddressText.includes(abbr))) return true;
    }

    // Check reverse - if any abbreviation is in our state name
    for (const [stateName, abbrs] of Object.entries(stateAbbreviations)) {
      if (abbrs.includes(normalizedState) && normalizedAddressText.includes(stateName)) return true;
    }

    return false;
  });

  const places: { id: string; address: string; escapedAddress: string }[] =
    filteredByState?.map((place) => {
      const address = buildFriendlyAddress(place);
      return {
        id: place.id,
        address: address,
        escapedAddress: escapeForSelector(address)
      };
    }) || [];

  // Determine if we should show the dropdown
  const shouldShowDropdown = !disabled && value.length > 0 && isEdition && !isLoadingDetails;

  if (!isEdition && !disabled) {
    return (
      <Row align="flex-start" justify="space-between" gap={16} className="w-full">
        <div className="flex items-center opacity-50 text-black w-[calc(100%-56px)] !text-base !rounded bg-white h-[40px] px-4 text-left shadow-sm focus:outline-none border border-solid border-[#0000005c]">
          <p className="whitespace-nowrap truncate w-[99%]">{selectedPlace?.address}</p>
        </div>
        <button
          onClick={handleEditPlace}
          className="w-10 h-10 bg-white border text-[#212121] hover:text-white hover:bg-blue transition-colors duration-150 rounded-full cursor-pointer flex items-center justify-center"
        >
          <FontAwesomeIcon icon={faPencil} size="sm" fixedWidth />
        </button>
      </Row>
    );
  }

  return (
    <div className="w-full">
      {/* Show protection error if any */}
      {protectionError && (
        <div className="mb-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-red-500">⚠️</span>
            <span>API Protection: {protectionError}</span>
          </div>
          <p className="mt-1 text-xs text-red-600">
            Wait a moment before trying again. This protects against excessive API usage.
          </p>
        </div>
      )}

      <Command shouldFilter={false} className="overflow-visible">
        <div className="w-full flex items-center gap-3 relative">
          <div className="relative flex-1">
            <CommandInput
              ref={inputRef}
              value={value}
              onValueChange={onChangeLocationInput}
              placeholder="Ingresa la dirección"
              className={classNames(
                '!text-base !rounded w-full bg-white h-[40px] px-4 pl-10 text-left shadow-sm focus:outline-none border border-solid',
                {
                  'border-[#0000005c]': !isFetching && !isLoadingDetails && !protectionError,
                  'border-blue-400': isFetching || isLoadingDetails,
                  'border-red-400': protectionError,
                  '!w-[calc(100%-56px)]': selectedPlace?.address && !isEdition
                }
              )}
              disabled={disabled || isLoadingDetails || !!protectionError}
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              {isFetching || isLoadingDetails ? (
                <FontAwesomeIcon icon={faSpinner} spin size="sm" />
              ) : (
                <FontAwesomeIcon icon={faSearch} size="sm" />
              )}
            </div>
          </div>
          {selectedPlace?.address && !disabled && (
            <button
              onClick={handleCancelEdition}
              className="w-10 h-10 flex items-center justify-center bg-white border border-gray-200 rounded-full text-gray-600 hover:bg-blue-500 hover:text-white transition-all duration-200 ease-in-out shadow-sm"
              disabled={isLoadingDetails}
            >
              <FontAwesomeIcon icon={faXmark} size="sm" fixedWidth />
            </button>
          )}
        </div>

        {/* Show dropdown when editing, with proper results or loading state */}
        {shouldShowDropdown && !protectionError && (
          <div className="relative animate-in fade-in-0 zoom-in-95">
            <CommandList>
              <div className="absolute top-0 z-50 w-full">
                <CommandGroup className="h-auto z-50 overflow-hidden rounded-lg border border-gray-200 shadow-lg bg-white max-h-[200px] overflow-y-auto">
                  {isFetching && (
                    <div className="flex items-center justify-center p-4 text-gray-500">
                      <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                      <span>Buscando direcciones...</span>
                    </div>
                  )}

                  {!isFetching &&
                    !error &&
                    places.length > 0 &&
                    places.map((place) => (
                      <CommandItem
                        value={place.escapedAddress}
                        onSelect={() => handleSelectPlace(place.id)}
                        key={place.id}
                        onMouseDown={(e) => e.preventDefault()}
                        className="px-4 py-2 text-gray-700 hover:bg-gray-100 cursor-pointer transition-colors duration-150 text-base"
                      >
                        {place.address}
                      </CommandItem>
                    ))}

                  {!isFetching && !error && places.length === 0 && isSuccess && (
                    <div className="px-4 py-4 text-gray-500 text-base flex items-center justify-center">
                      <div className="text-center">
                        <p>No se encontraron direcciones</p>
                        <p className="text-sm mt-1">Intenta con una búsqueda diferente</p>
                      </div>
                    </div>
                  )}
                </CommandGroup>
              </div>
            </CommandList>
          </div>
        )}

        {isLoadingDetails && (
          <div className="mt-2 text-center text-sm text-gray-500">
            <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
            <span>Obteniendo información detallada...</span>
          </div>
        )}
      </Command>
    </div>
  );
};
